import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronDown, ChevronUp, Search, CheckCircle2, Save, PiggyBank, X } from 'lucide-react';
import {
  calcInvoiceLine,
  calcInvoiceTotals,
  determineSupplyType,
  gstinStateCode,
  amountInWords,
  formatINR,
  formatWeight,
  money,
  round2,
  toAmount,
} from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { enqueueDraft } from '../../lib/offlineQueue.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField } from '../../components/ui/Field.jsx';
import { WeightInput } from '../../components/ui/WeightInput.jsx';

const DEFAULT_HSN_CODE = '7113';
const mobilePattern = /^[6-9]\d{9}$/;

function newItemRow() {
  return {
    key: crypto.randomUUID(),
    itemName: '',
    hsnId: '',
    gstRateOverride: '',
    purityId: '',
    purityLabel: '',
    grossWeight: '0',
    netWeight: '0',
    ratePerGram: '0',
    wastagePercentage: '0',
    makingType: 'per_gram',
    makingValue: '0',
    stoneCharges: '0',
    otherCharges: '0',
    itemDiscount: '0',
    expanded: false,
  };
}

function newOldGoldRow() {
  return { key: crypto.randomUUID(), description: '', purityId: '', purityLabel: '', grossWeight: '0', netWeight: '0', ratePerGram: '0' };
}

function newPaymentRow(mode = 'cash', amount = '0') {
  return { key: crypto.randomUUID(), mode, amount: String(amount), referenceNo: '' };
}

export default function InvoiceForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const shopQuery = useQuery({ queryKey: ['shop-settings'], queryFn: () => api.get('/shop-settings') });
  const hsnQuery = useQuery({ queryKey: ['masters', 'hsn'], queryFn: () => api.get('/masters/hsn-codes') });
  const puritiesQuery = useQuery({ queryKey: ['masters', 'purities'], queryFn: () => api.get('/masters/purities') });
  const ratesQuery = useQuery({ queryKey: ['rates', 'today'], queryFn: () => api.get('/rates/today') });

  const [customer, setCustomer] = useState({ id: null, name: '', mobile: '', address: '', gstin: '', pan: '', stateCode: null });
  const [items, setItems] = useState([newItemRow()]);
  const [oldGold, setOldGold] = useState([]);
  const [discountAmount, setDiscountAmount] = useState('0');
  const [chitAdjustValue, setChitAdjustValue] = useState('0');
  const [chitAccountId, setChitAccountId] = useState(null);
  const [chitAccountLabel, setChitAccountLabel] = useState(null);
  const [chitLookupError, setChitLookupError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingAction, setPendingAction] = useState(null);

  // --- Customer lookup by mobile -------------------------------------------
  const debouncedMobile = useDebouncedValue(customer.mobile, 400);
  const canLookup = mobilePattern.test(debouncedMobile) && !customer.id;
  const lookupQuery = useQuery({
    queryKey: ['customers', 'by-mobile', debouncedMobile],
    queryFn: () => api.get(`/customers/by-mobile/${debouncedMobile}`),
    enabled: canLookup,
  });
  const matches = canLookup ? lookupQuery.data ?? [] : [];

  const selectCustomer = (c) => {
    setCustomer({
      id: c.id,
      name: c.name,
      mobile: c.mobile ?? '',
      address: c.address_line1 ?? '',
      gstin: c.gstin ?? '',
      pan: c.pan ?? '',
      stateCode: c.state_code ?? (c.gstin ? gstinStateCode(c.gstin) : null),
    });
  };

  const clearCustomerMatch = () => setCustomer((c) => ({ ...c, id: null, stateCode: null }));

  // --- Matured chit account, redeemed straight into this sale --------------
  // Once the account is picked, redeeming and closing it happens server-side
  // when this invoice is issued — no separate trip to the chit page.
  const maturedChitQuery = useQuery({
    queryKey: ['chit', 'matured-for-invoice', debouncedMobile],
    queryFn: () => api.get('/chit/accounts', { query: { status: 'matured', search: debouncedMobile, pageSize: 5 } }),
    enabled: mobilePattern.test(debouncedMobile) && !chitAccountId,
  });
  const maturedMatches = mobilePattern.test(debouncedMobile) && !chitAccountId ? maturedChitQuery.data?.data ?? [] : [];

  const selectChitAccount = async (account) => {
    setChitLookupError(null);
    try {
      const detail = await api.get(`/chit/accounts/${account.id}`);
      const isGold = detail.scheme_type === 'gold';
      const todaysRate = isGold
        ? ratesQuery.data?.purities?.find((p) => p.purityId === detail.scheme_purity_id)?.ratePerGram
        : null;
      if (isGold && !todaysRate) {
        setChitLookupError("Today's rate for this scheme's purity hasn't been entered yet — enter it before redeeming this account.");
        return;
      }
      const value = isGold
        ? money(detail.total_grams).times(todaysRate)
        : money(detail.total_paid).plus(detail.bonus_amount);
      setChitAccountId(detail.id);
      setChitAccountLabel(`${detail.scheme_no} — ${detail.scheme_name}`);
      setChitAdjustValue(toAmount(round2(value)));
    } catch (err) {
      setChitLookupError(err instanceof ApiClientError ? err.message : 'Could not load that chit account.');
    }
  };

  const clearChitAccount = () => {
    setChitAccountId(null);
    setChitAccountLabel(null);
    setChitAdjustValue('0');
  };

  // --- Purities / rates lookups ---------------------------------------------
  const purityById = useMemo(() => new Map((puritiesQuery.data ?? []).map((p) => [p.id, p])), [puritiesQuery.data]);
  const rateByPurityId = useMemo(
    () => new Map((ratesQuery.data?.purities ?? []).filter((p) => p.ratePerGram).map((p) => [p.purityId, p.ratePerGram])),
    [ratesQuery.data],
  );

  const applyPurity = (key, purityId) => {
    const purity = purityById.get(Number(purityId));
    const rate = rateByPurityId.get(Number(purityId));
    setItems((rows) =>
      rows.map((r) =>
        r.key === key
          ? { ...r, purityId, purityLabel: purity?.label ?? '', ratePerGram: rate ? String(rate) : r.ratePerGram }
          : r,
      ),
    );
  };

  const updateItem = (key, patch) => setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeItem = (key) => setItems((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));
  const addItem = () => setItems((rows) => [...rows, newItemRow()]);

  const updateOldGold = (key, patch) => setOldGold((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeOldGold = (key) => setOldGold((rows) => rows.filter((r) => r.key !== key));
  const addOldGold = () => setOldGold((rows) => [...rows, newOldGoldRow()]);

  const updatePayment = (key, patch) => setPayments((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removePayment = (key) => setPayments((rows) => rows.filter((r) => r.key !== key));
  const addPayment = () => setPayments((rows) => [...rows, newPaymentRow()]);

  // --- Live calculation, using the exact same functions the server uses ----
  const supplyType = determineSupplyType(shopQuery.data?.state_code, customer.stateCode || (customer.gstin ? gstinStateCode(customer.gstin) : null));

  const hsnLookup = (hsnId) => {
    const list = hsnQuery.data ?? [];
    return list.find((h) => h.id === Number(hsnId)) ?? list.find((h) => h.code === DEFAULT_HSN_CODE);
  };

  const isFlatHsn = (hsnId) => hsnLookup(hsnId)?.pricing_mode === 'flat';

  const lines = items.map((item) => {
    const hsn = hsnLookup(item.hsnId);
    const isFlat = hsn?.pricing_mode === 'flat';
    // Diamonds in particular don't always carry the HSN's default 3% — an
    // explicit rate on the line wins over whatever the HSN master says.
    const gstRate = item.gstRateOverride !== '' && item.gstRateOverride != null ? item.gstRateOverride : hsn?.gst_rate ?? '3';
    const calc = calcInvoiceLine({
      netWeight: isFlat ? 0 : item.netWeight,
      ratePerGram: isFlat ? 0 : item.ratePerGram,
      wastagePercentage: isFlat ? 0 : item.wastagePercentage,
      makingType: isFlat ? 'flat' : item.makingType,
      makingValue: isFlat ? 0 : item.makingValue,
      stoneCharges: item.stoneCharges,
      otherCharges: item.otherCharges,
      itemDiscount: item.itemDiscount,
      gstRate,
      supplyType,
    });
    return { source: item, hsnCode: hsn?.code ?? DEFAULT_HSN_CODE, calc };
  });

  const oldGoldValue = round2(
    oldGold.reduce((acc, g) => acc.plus(money(g.netWeight || 0).times(money(g.ratePerGram || 0))), money(0)),
  );

  const totals = calcInvoiceTotals(
    lines.map((l) => l.calc),
    { discountAmount, oldGoldValue: toAmount(oldGoldValue), chitAdjustValue },
  );

  const words = amountInWords(totals.amountPayable);
  const paidTotal = round2(payments.reduce((acc, p) => acc.plus(money(p.amount || 0)), money(0)));
  const balance = round2(money(totals.amountPayable).minus(paidTotal));

  const rateGateOpen = ratesQuery.data?.gateOpen;

  // --- Submit ----------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/invoices', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      if (chitAccountId) queryClient.invalidateQueries({ queryKey: ['chit'] });
      navigate(`/invoices/${result.invoice.id}`, { state: { justCreated: true } });
    },
    onError: (err, payload) => {
      // Only a draft-save queues offline — issuing needs a server-allocated
      // number at the moment it happens, so an offline "Issue" click stays a
      // hard error pointing at "save as draft" instead (docs/project-plan.html §13).
      if (err instanceof ApiClientError && err.isOffline && pendingAction === 'draft') {
        enqueueDraft({ endpoint: '/invoices', payload, docType: 'invoice', label: customer.name || 'Untitled invoice' })
          .then(() => navigate('/invoices', { state: { savedOffline: true } }))
          .catch(() => setSubmitError('Could not save this draft on this device either. Please try again.'));
        return;
      }
      if (err instanceof ApiClientError && err.details) setFieldErrors(err.details);
      setSubmitError(err instanceof ApiClientError ? err.message : 'Could not save this invoice. Please try again.');
    },
  });

  const buildPayload = (saveAsDraft = false) => ({
    invoiceType: 'tax_invoice',
    saveAsDraft,
    customerId: customer.id ?? undefined,
    customerName: customer.name,
    customerMobile: customer.mobile || undefined,
    customerAddress: customer.address || undefined,
    customerGstin: customer.gstin || undefined,
    customerPan: customer.pan || undefined,
    items: items.map((item) => {
      const flat = isFlatHsn(item.hsnId);
      return {
        itemName: item.itemName,
        hsnId: item.hsnId ? Number(item.hsnId) : undefined,
        gstRateOverride: item.gstRateOverride !== '' && item.gstRateOverride != null ? Number(item.gstRateOverride) : undefined,
        purityLabel: flat ? undefined : item.purityLabel || undefined,
        grossWeight: flat ? 0 : Number(item.grossWeight),
        netWeight: flat ? 0 : Number(item.netWeight),
        ratePerGram: flat ? 0 : Number(item.ratePerGram),
        wastagePercentage: flat ? 0 : Number(item.wastagePercentage),
        makingType: flat ? 'flat' : item.makingType,
        makingValue: flat ? 0 : Number(item.makingValue),
        stoneCharges: Number(item.stoneCharges),
        otherCharges: Number(item.otherCharges),
        itemDiscount: Number(item.itemDiscount),
      };
    }),
    discountAmount: Number(discountAmount),
    oldGold: oldGold.map((g) => ({
      description: g.description,
      purityId: g.purityId ? Number(g.purityId) : undefined,
      purityLabel: g.purityLabel || undefined,
      grossWeight: Number(g.grossWeight),
      netWeight: Number(g.netWeight),
      ratePerGram: Number(g.ratePerGram),
    })),
    chitAdjustValue: Number(chitAdjustValue),
    // Ignored server-side when saving as a draft — redeeming needs a real
    // invoice number, which a draft doesn't have yet.
    chitAccountId: chitAccountId ?? undefined,
    payments: payments.map((p) => ({ mode: p.mode, amount: Number(p.amount), referenceNo: p.referenceNo || undefined })),
    notes: notes || undefined,
  });

  const baseValid =
    !!customer.name.trim() &&
    items.every((i) =>
      i.itemName.trim() &&
      (isFlatHsn(i.hsnId) ? Number(i.otherCharges) > 0 : Number(i.netWeight) > 0 && Number(i.ratePerGram) > 0),
    );
  const canSubmit = baseValid && rateGateOpen !== false;
  const canSaveDraft = baseValid;

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    setPendingAction('issue');
    createMutation.mutate(buildPayload(false));
  };

  const onSaveDraft = () => {
    setSubmitError(null);
    setFieldErrors({});
    setPendingAction('draft');
    createMutation.mutate(buildPayload(true));
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 pb-28">
      <h1 className="font-display text-2xl font-semibold tracking-tight">New invoice</h1>

      {ratesQuery.data && !rateGateOpen ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          Today&rsquo;s rate hasn&rsquo;t been fully entered — billing is blocked until it is.
        </p>
      ) : null}

      {/* Customer ------------------------------------------------------- */}
      <section className="card flex flex-col gap-4 p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Customer</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <TextField
              label="Mobile"
              inputMode="numeric"
              value={customer.mobile}
              onChange={(e) => {
                if (e.target.value !== customer.mobile) clearChitAccount();
                setCustomer((c) => ({ ...c, mobile: e.target.value, id: c.id && e.target.value !== c.mobile ? null : c.id }));
              }}
              hint={customer.id ? 'Matched to a saved customer' : 'Type 10 digits to search'}
            />
            {matches.length > 0 ? (
              <div className="absolute z-10 mt-1 w-full rounded-card border border-border bg-surface shadow-lg">
                {matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => selectCustomer(m)}
                    className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-surface-2"
                  >
                    <Search size={14} className="text-muted" aria-hidden="true" />
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted">{m.city}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <TextField label="Name" required value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
          <TextField label="Address" value={customer.address} onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))} />
          <TextField
            label="GSTIN"
            value={customer.gstin}
            disabled={!!customer.id}
            onChange={(e) => setCustomer((c) => ({ ...c, gstin: e.target.value }))}
            error={fieldErrors.customerGstin}
            hint={customer.id ? 'From the saved customer record' : 'Leave blank for a walk-in (B2C) sale'}
          />
          <TextField
            label="PAN"
            value={customer.pan}
            disabled={!!customer.id && !!customer.pan}
            onChange={(e) => setCustomer((c) => ({ ...c, pan: e.target.value.toUpperCase() }))}
            error={fieldErrors.customerPan}
            hint="Required once this bill reaches ₹2,00,000 (Rule 114B)"
          />
        </div>
        {customer.id ? (
          <button type="button" onClick={clearCustomerMatch} className="self-start text-sm text-primary hover:underline">
            Not this customer — enter details manually
          </button>
        ) : null}
        <p className="text-xs text-muted">
          {supplyType === 'inter' ? 'Inter-state sale — IGST applies.' : 'Intra-state sale — CGST + SGST applies.'}
        </p>
      </section>

      {/* Matured chit account ---------------------------------------------- */}
      {chitAccountId ? (
        <section className="card flex items-center justify-between gap-3 border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center gap-2.5">
            <PiggyBank size={18} className="text-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium">Redeeming chit account {chitAccountLabel}</p>
              <p className="text-xs text-muted">
                {formatINR(chitAdjustValue)} applied as the chit adjustment below — the account closes automatically once this invoice is issued.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={clearChitAccount}
            className="flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger"
            aria-label="Don't redeem a chit account on this invoice"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </section>
      ) : maturedMatches.length > 0 ? (
        <section className="card flex flex-col gap-2.5 p-4">
          <div className="flex items-center gap-2">
            <PiggyBank size={18} className="text-primary" aria-hidden="true" />
            <h2 className="text-sm font-semibold">
              {maturedMatches.length === 1 ? 'This customer has a matured chit account' : 'This customer has matured chit accounts'}
            </h2>
          </div>
          <div className="flex flex-col gap-1.5">
            {maturedMatches.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => selectChitAccount(a)}
                className="flex items-center justify-between rounded-card border border-border px-3 py-2 text-left text-sm hover:bg-surface-2"
              >
                <span className="font-medium">{a.scheme_no} — {a.scheme_name}</span>
                <span className="text-primary">Redeem into this invoice</span>
              </button>
            ))}
          </div>
          {chitLookupError ? <p className="text-xs text-danger">{chitLookupError}</p> : null}
        </section>
      ) : null}

      {/* Items ------------------------------------------------------------ */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Items</h2>
          <Button type="button" variant="secondary" size="sm" onClick={addItem}>
            <Plus size={15} aria-hidden="true" /> Add item
          </Button>
        </div>

        {items.map((item, index) => {
          const line = lines[index].calc;
          const flat = isFlatHsn(item.hsnId);
          return (
            <div key={item.key} className="card flex flex-col gap-3 p-4">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <TextField
                    label="Item name"
                    required
                    value={item.itemName}
                    onChange={(e) => updateItem(item.key, { itemName: e.target.value })}
                  />
                </div>
                {items.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeItem(item.key)}
                    className="mt-7 flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger"
                    aria-label="Remove item"
                  >
                    <Trash2 size={18} aria-hidden="true" />
                  </button>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {flat ? (
                  <div />
                ) : (
                  <SelectField label="Purity" value={item.purityId} onChange={(e) => applyPurity(item.key, e.target.value)}>
                    <option value="">—</option>
                    {(puritiesQuery.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.metal} {p.label}
                      </option>
                    ))}
                  </SelectField>
                )}
                <SelectField label="HSN code" value={item.hsnId} onChange={(e) => updateItem(item.key, { hsnId: e.target.value })}>
                  <option value="">Gold and silver ornaments — 7113 (default)</option>
                  {(hsnQuery.data ?? []).map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.description} — {h.code} ({h.gst_rate}%)
                    </option>
                  ))}
                </SelectField>
              </div>

              {flat ? (
                <TextField
                  label="Flat rate (Rs.)"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  required
                  value={item.otherCharges}
                  onChange={(e) => updateItem(item.key, { otherCharges: e.target.value })}
                  hint="This HSN isn't priced by weight - enter the item's rupee price directly; GST applies at the HSN's rate."
                />
              ) : (
                <>
              <div className="grid grid-cols-2 gap-3">
                <WeightInput label="Gross weight" value={item.grossWeight} onChange={(v) => updateItem(item.key, { grossWeight: v })} />
                <WeightInput label="Net weight" required value={item.netWeight} onChange={(v) => updateItem(item.key, { netWeight: v })} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="Rate per gram"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  required
                  value={item.ratePerGram}
                  onChange={(e) => updateItem(item.key, { ratePerGram: e.target.value })}
                />
                <TextField
                  label="Wastage %"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  max="100"
                  value={item.wastagePercentage}
                  onChange={(e) => updateItem(item.key, { wastagePercentage: e.target.value })}
                  hint="Extra weight billed for melting loss"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <SelectField label="Making" value={item.makingType} onChange={(e) => updateItem(item.key, { makingType: e.target.value })}>
                  <option value="per_gram">₹/g</option>
                  <option value="flat">Flat ₹</option>
                </SelectField>
                <TextField
                  label={' '}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={item.makingValue}
                  onChange={(e) => updateItem(item.key, { makingValue: e.target.value })}
                />
              </div>
                </>
              )}

              <button
                type="button"
                onClick={() => updateItem(item.key, { expanded: !item.expanded })}
                className="flex items-center gap-1 self-start text-sm text-muted hover:text-content"
              >
                {item.expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                Stones, other charges &amp; discount
              </button>

              {item.expanded ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <TextField
                    label="Stone charges"
                    type="number"
                    inputMode="decimal"
                    value={item.stoneCharges}
                    onChange={(e) => updateItem(item.key, { stoneCharges: e.target.value })}
                  />
                  {flat ? null : (
                    <TextField
                      label="Other charges"
                      type="number"
                      inputMode="decimal"
                      value={item.otherCharges}
                      onChange={(e) => updateItem(item.key, { otherCharges: e.target.value })}
                    />
                  )}
                  <TextField
                    label="Item discount"
                    type="number"
                    inputMode="decimal"
                    value={item.itemDiscount}
                    onChange={(e) => updateItem(item.key, { itemDiscount: e.target.value })}
                  />
                  <TextField
                    label="GST % override"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    max="100"
                    value={item.gstRateOverride}
                    onChange={(e) => updateItem(item.key, { gstRateOverride: e.target.value })}
                    hint={`Blank uses the HSN's ${hsnLookup(item.hsnId)?.gst_rate ?? '3'}% — set this for diamonds/stones billed at a different rate`}
                  />
                </div>
              ) : null}

              {Number(line.wastagePercentage) > 0 ? (
                <p className="text-xs text-muted">
                  Billed weight {formatWeight(line.billedWeight)} (net {formatWeight(item.netWeight)} + {line.wastagePercentage}% wastage)
                </p>
              ) : null}

              <div className="flex items-center justify-between border-t border-border pt-2.5 text-sm">
                <span className="text-muted">
                  Taxable {formatINR(line.taxableValue)} · GST {line.gstRate}%
                </span>
                <span className="tabular font-semibold">{formatINR(line.lineTotal)}</span>
              </div>
            </div>
          );
        })}
      </section>

      {/* Old gold ----------------------------------------------------------- */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Old gold exchanged</h2>
          <Button type="button" variant="secondary" size="sm" onClick={addOldGold}>
            <Plus size={15} aria-hidden="true" /> Add
          </Button>
        </div>
        {oldGold.map((g) => (
          <div key={g.key} className="card flex flex-col gap-3 p-4">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <TextField label="Description" value={g.description} onChange={(e) => updateOldGold(g.key, { description: e.target.value })} />
              </div>
              <button
                type="button"
                onClick={() => removeOldGold(g.key)}
                className="mt-7 flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger"
                aria-label="Remove"
              >
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <WeightInput label="Gross weight" value={g.grossWeight} onChange={(v) => updateOldGold(g.key, { grossWeight: v })} />
              <WeightInput label="Net weight" value={g.netWeight} onChange={(v) => updateOldGold(g.key, { netWeight: v })} />
            </div>
            <TextField
              label="Rate per gram"
              type="number"
              inputMode="decimal"
              value={g.ratePerGram}
              onChange={(e) => updateOldGold(g.key, { ratePerGram: e.target.value })}
            />
          </div>
        ))}
      </section>

      {/* Discount / chit ------------------------------------------------- */}
      <section className="card grid grid-cols-2 gap-3 p-5">
        <TextField
          label="Invoice discount (₹)"
          type="number"
          inputMode="decimal"
          value={discountAmount}
          onChange={(e) => setDiscountAmount(e.target.value)}
        />
        <TextField
          label="Chit adjustment (₹)"
          type="number"
          inputMode="decimal"
          value={chitAdjustValue}
          onChange={(e) => setChitAdjustValue(e.target.value)}
          hint={
            chitAccountId
              ? 'Auto-filled from the selected chit account above — adjust it if the agreed redemption value differs'
              : "A manual adjustment without linking a chit account here won't redeem or close anything — pick a matured account above to do both automatically"
          }
        />
      </section>

      {/* Payments ---------------------------------------------------------- */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Payments</h2>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!(Number(totals.amountPayable) > 0)}
              onClick={() => setPayments([newPaymentRow('cash', totals.amountPayable)])}
            >
              Mark fully paid
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={addPayment}>
              <Plus size={15} aria-hidden="true" /> Add
            </Button>
          </div>
        </div>
        {payments.map((p) => (
          <div key={p.key} className="card flex items-end gap-3 p-4">
            <SelectField label="Mode" value={p.mode} onChange={(e) => updatePayment(p.key, { mode: e.target.value })} className="flex-1">
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
              <option value="netbanking">Net banking</option>
              <option value="cheque">Cheque</option>
            </SelectField>
            <TextField
              label="Amount"
              type="number"
              inputMode="decimal"
              value={p.amount}
              onChange={(e) => updatePayment(p.key, { amount: e.target.value })}
              className="flex-1"
            />
            <button
              type="button"
              onClick={() => removePayment(p.key)}
              className="flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger"
              aria-label="Remove payment"
            >
              <Trash2 size={18} aria-hidden="true" />
            </button>
          </div>
        ))}
      </section>

      {/* Totals -------------------------------------------------------------- */}
      <section className="card ml-auto w-full max-w-sm p-5">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Taxable value</dt><dd className="tabular">{formatINR(totals.taxableValue)}</dd></div>
          {Number(totals.cgstAmount) > 0 ? (
            <>
              <div className="flex justify-between"><dt className="text-muted">CGST</dt><dd className="tabular">{formatINR(totals.cgstAmount)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted">SGST</dt><dd className="tabular">{formatINR(totals.sgstAmount)}</dd></div>
            </>
          ) : null}
          {Number(totals.igstAmount) > 0 ? (
            <div className="flex justify-between"><dt className="text-muted">IGST</dt><dd className="tabular">{formatINR(totals.igstAmount)}</dd></div>
          ) : null}
          <div className="flex justify-between"><dt className="text-muted">Round off</dt><dd className="tabular">{formatINR(totals.roundOff)}</dd></div>
          <div className="mt-1 flex justify-between border-t border-border pt-2 text-base font-semibold"><dt>Grand total</dt><dd className="tabular">{formatINR(totals.grandTotal)}</dd></div>
          {Number(totals.oldGoldValue) > 0 ? (
            <div className="flex justify-between"><dt className="text-muted">Old gold</dt><dd className="tabular">−{formatINR(totals.oldGoldValue)}</dd></div>
          ) : null}
          {Number(totals.amountPayable) < 0 ? (
            <div className="flex justify-between text-base font-semibold text-info">
              <dt>Refund due to customer</dt><dd className="tabular">{formatINR(Math.abs(totals.amountPayable))}</dd>
            </div>
          ) : (
            <div className="flex justify-between text-base font-semibold text-primary"><dt>Amount payable</dt><dd className="tabular">{formatINR(totals.amountPayable)}</dd></div>
          )}
          {payments.length ? (
            <div className="flex justify-between text-warning"><dt>Balance</dt><dd className="tabular">{formatINR(balance)}</dd></div>
          ) : null}
        </dl>
        {Number(totals.amountPayable) < 0 ? (
          <p className="mt-3 rounded-card border border-info/30 bg-info/10 px-3 py-2 text-xs text-info">
            Old gold exchanged is worth more than this sale — the shop owes the customer the difference. Save this
            invoice, then record what&rsquo;s actually handed back from its page.
          </p>
        ) : null}
        <p className="mt-3 text-xs italic text-muted">{words}</p>
      </section>

      {submitError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </p>
      ) : null}

      {chitAccountId ? (
        <p className="text-xs text-muted">
          The chit account only redeems and closes when this invoice is issued — saving as a draft leaves it matured and untouched.
        </p>
      ) : null}

      {/* Sticky action bar */}
      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center justify-between gap-3 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <div className={clsx('tabular text-lg font-semibold', Number(totals.amountPayable) < 0 && 'text-info')}>
          {Number(totals.amountPayable) < 0 ? `Refund due ${formatINR(Math.abs(totals.amountPayable))}` : formatINR(totals.amountPayable)}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            disabled={!canSaveDraft}
            loading={createMutation.isPending && pendingAction === 'draft'}
            onClick={onSaveDraft}
          >
            <Save size={18} aria-hidden="true" /> Save as draft
          </Button>
          <Button type="submit" size="lg" disabled={!canSubmit} loading={createMutation.isPending && pendingAction === 'issue'}>
            <CheckCircle2 size={18} aria-hidden="true" /> Save invoice
          </Button>
        </div>
      </div>
    </form>
  );
}
